import React, { useState, useRef, useEffect } from 'react';

interface ProductSpec {
  label: string;
  value: string;
}

export interface ProductProps {
  id: string;
  name: string;
  productCode: string;
  image: string;
  description: string;
  specs: ProductSpec[];
  price: number;
  categoryIds?: string[];
}

const ProductCard: React.FC<ProductProps> = ({
  id,
  name,
  productCode,
  image,
  description,
  specs,
  price
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Card appear animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    const el = cardRef.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, []);

  // Format price to show as South African Rand
  const formattedPrice = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(price);

  return (
    <div 
      ref={cardRef}
      className={`bg-white rounded-lg overflow-hidden shadow-md hover-float transition-all duration-500 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="relative overflow-hidden h-56 parallax-container">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover transition-all duration-700 hover:scale-110"
          crossOrigin="anonymous"
          onError={(e) => {
            console.error('Failed to load image:', image);
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300?text=No+Image';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />
        <div className="absolute top-3 right-3 bg-accent text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg transform rotate-2 hover:rotate-0 transition-all duration-300">
          {productCode}
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-heading font-bold text-text">{name}</h3>
          <span className="text-primary font-bold">{formattedPrice}</span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2">{description}</p>
        
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary font-medium flex items-center focus:outline-none transition-all duration-300 hover:text-primary/80 hover:pl-1"
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'View Less' : 'View Details'}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-5 w-5 ml-1 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        
        {/* Expanded Details */}
        <div 
          className={`mt-4 pt-4 border-t border-gray-200 overflow-hidden transition-all duration-500 ease-in-out ${
            isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 border-t-0'
          }`}
        >
          <h4 className="font-heading font-semibold mb-3 text-sm uppercase text-gray-500">Specifications</h4>
          <div className="space-y-2">
            {specs.map((spec, index) => (
              <div 
                key={index} 
                className="flex justify-between text-sm"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="text-gray-600">{spec.label}</span>
                <span className="font-medium text-text">{spec.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2 justify-between">
            <a 
              href="#contact" 
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg btn-shine flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Us
            </a>
            <button className="p-2 text-gray-500 hover:text-primary transition-all duration-300 rounded-full hover:bg-gray-100" aria-label="Share">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard; 